import {
  Button,
  Icon,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import * as React from 'react';
import { compose } from 'recompose';
import { useWorklistsReport } from '@/app/components/Reporting/Collaboration/WorklistsReport/useWorklistReport';
import { useCallback, useMemo } from 'react';
import SelectFilter from '@/app/components/WorkflowsReport/components/KendoFilters/SelectFilter';
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';
import { ToolbarManagerAndHr } from '@/app/components/WorkflowsReport/components/ToolbarManagerAndHr';
import ColumnMenu from '@/app/components/Kendo/ColumnMenu';
import {
  ExcelExport,
  ExcelExportColumn,
} from '@progress/kendo-react-excel-export';
import translate from '@/app/utils/translate';

const styles = (theme: Theme) => ({
  title: {
    marginBottom: theme.spacing.unit,
  },
  root: {
    minHeight: '250px',
    height: '100%',
  },
  noDataOrScope: {
    padding: theme.spacing.unit * 2,
  },
  navigation: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gridTemplateAreas: '"previous current next"',
  },
  navigationPrevious: {
    gridArea: 'previous',
    justifySelf: 'start',
  },
  navigationCurrent: {
    gridArea: 'current',
    justifySelf: 'center',
  },
  navigationNext: {
    gridArea: 'next',
    justifySelf: 'end',
  },
  chartDescription: {
    textAlign: 'center' as const, // Casting to fix odd TS error
  },

  toolbar: {
    display: 'grid',
    gridTemplateAreas: `"scope download"`,
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
  },
  toolbarScope: {
    gridArea: 'scope',
  },
  toolbarDownload: {
    gridArea: 'download',
    justifySelf: 'end',
    borderLeft: 'solid 1px rgba(0, 0, 0, 0.08)',
    paddingLeft: '8px',
  },
});

type OwnProps = {};
type InnerProps = WithStyles<typeof styles>;
type PropsType = OwnProps & InnerProps;

const enhance = compose<PropsType, OwnProps>(
  withStyles(styles, { withTheme: true }),
);

export const WorklistsReport = enhance((props: PropsType) => {
  const { classes } = props;

  const {
    reportName,
    gridData,
    excelData,
    toolbarSettings,
    reportSettings,
    filterCellOptions: { unitOptions},
    onColumnsSubmit,
    onDataStateChange,
    loading,
    error,
  } = useWorklistsReport();
  const { columns, dataState } = reportSettings.current;

  const columnSelectFilter = useCallback(
    fieldId => {
      switch (fieldId) {
        case 'unitNo':
          return SelectFilter(unitOptions, 'Select unit');
        default:
          return undefined;
      }
    },
    [unitOptions],
  );

  const _exporter = React.createRef<ExcelExport>();

  const excelExport = useMemo(() => {
    return {
      data: excelData.current,
      fileName: excelData.fileName,
    };
  }, [excelData]);

  const onExcelExport = () => {
    if (_exporter.current) {
      _exporter.current.save();
    }
  };

  if (loading) {
    return <div>{loading}</div>;
  }
  if (error) {
    return <div>{'error'}</div>;
  }

  if (!gridData) {
    return <div>{'no data'}</div>;
  }

  const DownloadButton = () => {
    return (
      <Button onClick={onExcelExport}>
        {translate.t('laDownload')} &nbsp;{' '}
        <Icon fontSize="small">cloud_download</Icon>
      </Button>
    );
  };

  return (
    <div>
      <Typography className={classes.title} variant="h6">
        {reportName}
      </Typography>
      <Grid
        data={process(gridData, dataState)}
        onDataStateChange={onDataStateChange}
        {...dataState}
        sortable={true}
        resizable={true}
        pageable={true}
        groupable={false}
      >
        <GridToolbar>
          <div className={classes.toolbar}>
            <div className={classes.toolbarScope}>
              <ToolbarManagerAndHr {...toolbarSettings} />
            </div>
            <div className={classes.toolbarDownload}>
              <DownloadButton />
            </div>
          </div>
        </GridToolbar>
        {columns.map(
          (column: any, idx: number) =>
            column.show && (
              <GridColumn
                {...column}
                key={idx}
                columnMenu={(otherProps: any) => (
                  <ColumnMenu
                    {...otherProps}
                    columns={columns}
                    onColumnsSubmit={onColumnsSubmit}
                    filterUI={columnSelectFilter(column.field)}
                  />
                )}
              />
            ),
        )}
      </Grid>
      <ExcelExport {...excelExport} ref={_exporter}>
        {columns
          .filter(column => column.field !== 'empId')
          .map((column: any, idx: number) => {
            return <ExcelExportColumn key={`column-${idx}`} {...column} />;
          })}
      </ExcelExport>
    </div>
  );
});
